import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import axiosInstance from '../../../axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import CheckboxInput from '../../common/Form/CheckboxInput';
import Button from 'apollo-react/components/Button';
import MenuItem from 'apollo-react/components/MenuItem';
import CircularProgress from 'apollo-react/components/CircularProgress';
import { FormikSelect, FormikTextField } from '../../common/FormikComponent';
import Grid from 'apollo-react/components/Grid';

import PrivilegesSections from './PrivilegesSections';
import CustomUsersModal from './CustomUsersModal/CustomUsersModal';
import useRolesApi from '../../../hooks/api/roles/useRolesApi';
import styles from './form.module.css';
import { PRIVILEGE_TYPES } from '../../../constants/role.constant';
import { capitalizeFirstLetter } from '../../utils/stringUtils';
import useUnsavedChangesWarning from '../../../hooks/ui/navigation/useUnsavedChangesWarning';
import {
  computeSectionSelectionState,
  processCheckboxChangeWithReadEnforcement,
  initializeManuallyCheckedReads,
} from '../../../hooks/utils/privilegesUtils';

const privilegesTypes = Object.values(PRIVILEGE_TYPES);

const PrivilegesForm = ({
  caseProp,
  userId,
  roleId,
  privilegesOnly = false,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [privileges, setPrivileges] = useState([]);
  const [selectedPrivileges, setSelectedPrivileges] = useState([]);
  const [boxesState, setBoxesState] = useState([]);
  const [published, setPublished] = useState(false);
  const [sections, setSections] = useState([]);
  const [isSectionSelected, setIsSectionSelected] = useState({});
  const [roleGroupType, setRoleGroupType] = useState('');
  const [pendingGrantedPrivileges, setPendingGrantedPrivileges] =
    useState(null);
  const [showCustomUsersModal, setShowCustomUsersModal] = useState(false);
  const [customUsersData, setCustomUsersData] = useState(null);
  const [initialRolePrivileges, setInitialRolePrivileges] = useState([]);
  const [manuallyCheckedReads, setManuallyCheckedReads] = useState(new Set());
  const initialRoleDataRef = useRef({
    name: '',
    description: '',
    published: false,
    roleGroupType: '',
  });

  const { getUsersWithCustomPrivilegesForRole } = useRolesApi();

  const { id: paramId } = useParams();
  const id = userId || roleId || paramId;
  const { t, ready } = useTranslation();

  const isDirty = useMemo(() => {
    const init = initialRoleDataRef.current;
    return (
      name !== init.name ||
      description !== init.description ||
      published !== init.published ||
      roleGroupType !== init.roleGroupType ||
      JSON.stringify([...selectedPrivileges].sort()) !==
        JSON.stringify([...initialRolePrivileges].sort())
    );
  }, [
    name,
    description,
    published,
    roleGroupType,
    selectedPrivileges,
    initialRolePrivileges,
  ]);

  const { navigateBack } = useUnsavedChangesWarning(isDirty);

  useEffect(() => {
    if (caseProp === 'role' && !roleGroupType) {
      return;
    }
    let query = '';
    if (roleGroupType === PRIVILEGE_TYPES.INTERNAL) {
      query = 'is_internal=true';
    } else if (roleGroupType === PRIVILEGE_TYPES.EXTERNAL) {
      query = 'is_external=true';
    }
    axiosInstance
      .get(`/api/privileges?${query}`)
      .then((response) => {
        setPrivileges(response.data);
        setBoxesState(new Array(response.data.length).fill(false));

        const allPriviliges = response.data;
        const currentResource = allPriviliges[0].Resource.title;
        const currentSections = [{}];
        currentSections.pop();
        divideSectionsFromPriviliges(
          allPriviliges,
          currentResource,
          currentSections
        );
        setSections(currentSections);
      })
      .catch((error) => {
        console.error('error fetching the privileges', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, roleGroupType]);

  // Separate useEffect for privilegesOnly mode to avoid interfering with normal role editing
  useEffect(() => {
    if (privilegesOnly && id && caseProp === 'role') {
      axiosInstance
        .get(`/api/roles-privileges/${id}`)
        .then((response) => {
          // For read-only mode, only show the privileges this role has
          const rolePrivileges = response.data.map((item) => item.Privilege);
          setPrivileges(rolePrivileges);
          setBoxesState(new Array(rolePrivileges.length).fill(true)); // All are selected
          setSelectedPrivileges(rolePrivileges.map((priv) => priv.id));

          // Create sections from only the role's privileges
          if (rolePrivileges.length > 0) {
            const currentResource = rolePrivileges[0].Resource.title;
            const currentSections = [{}];
            currentSections.pop();
            divideSectionsFromPriviliges(
              rolePrivileges,
              currentResource,
              currentSections
            );
            setSections(currentSections);
          }
        })
        .catch((error) => {
          console.error(
            'error fetching role privileges for read-only mode',
            error
          );
        });
    }
  }, [privilegesOnly, id, caseProp]);

  const divideSectionsFromPriviliges = (
    allPriviliges,
    currentResource,
    currentSections
  ) => {
    const groupedByCategory = {};

    allPriviliges.forEach((priv) => {
      const resourceTitle = priv.Resource.title;
      const titleParts = resourceTitle.split(' / ');
      const category = titleParts[0] || 'Other';
      const resource = titleParts[1] || resourceTitle;

      if (!groupedByCategory[category]) {
        groupedByCategory[category] = {};
      }

      if (!groupedByCategory[category][resource]) {
        groupedByCategory[category][resource] = [];
      }

      groupedByCategory[category][resource].push(priv);
    });

    const sortedCategories = Object.keys(groupedByCategory).sort();
    const result = {};

    sortedCategories.forEach((category) => {
      const sortedResources = Object.keys(groupedByCategory[category]).sort();
      result[category] = {};
      sortedResources.forEach((resource) => {
        result[category][resource] = groupedByCategory[category][resource];
      });
    });

    Object.assign(currentSections, result);
  };

  useEffect(() => {
    if (id && caseProp === 'role' && !privilegesOnly) {
      axiosInstance
        .get(`/api/roles/${id}`)
        .then((response) => {
          const rgt = response.data.is_internal
            ? PRIVILEGE_TYPES.INTERNAL
            : response.data.is_external
              ? PRIVILEGE_TYPES.EXTERNAL
              : '';
          setName(response.data.name);
          setPublished(response.data.published);
          setDescription(response.data.description);
          setRoleGroupType(rgt);
          initialRoleDataRef.current = {
            name: response.data.name,
            description: response.data.description,
            published: response.data.published,
            roleGroupType: rgt,
          };
        })
        .catch((err) => {
          console.log(err);
        });

      axiosInstance
        .get(`/api/roles-privileges/${id}`)
        .then((response) => {
          const grantedPrivileges = response.data.map(
            (priv) => priv.Privilege.id
          );
          // store granted privileges to be processed when privileges are loaded
          setPendingGrantedPrivileges(grantedPrivileges);
          // Store initial privileges for delta calculation
          setInitialRolePrivileges(grantedPrivileges);
        })
        .catch((error) => {
          console.error('error fetching the privileges', error);
        });
    } else if (id && caseProp === 'user') {
      axiosInstance
        .get(`/api/users-priviliges/${id}`)
        .then((resp) => {
          const grantedPrivileges = resp.data.map((priv) => priv.Privilege.id);
          setPendingGrantedPrivileges(grantedPrivileges);
          setInitialRolePrivileges(grantedPrivileges);
          setRoleGroupType(PRIVILEGE_TYPES.INTERNAL);
        })
        .catch((err) => {
          console.log(err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, caseProp]);

  // process pending granted privileges when privileges are loaded
  useEffect(() => {
    if (
      pendingGrantedPrivileges &&
      privileges.length > 0 &&
      Object.keys(sections).length > 0
    ) {
      handleGrantedPriviligesWithCheckedBoxesAndSections(
        pendingGrantedPrivileges
      );
      setPendingGrantedPrivileges(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGrantedPrivileges, privileges, sections]);

  const handleGrantedPriviligesWithCheckedBoxesAndSections = (
    grantedPrivileges
  ) => {
    setBoxesState(() => {
      return privileges.map((privilege) =>
        grantedPrivileges.includes(privilege.id)
      );
    });
    setSelectedPrivileges(grantedPrivileges);
    setManuallyCheckedReads(
      initializeManuallyCheckedReads(sections, grantedPrivileges)
    );

    Object.entries(sections).forEach(([category, resources]) => {
      Object.keys(resources).forEach((resource) => {
        setSectionInitialSelection(category, resource);
      });
    });
  };
  const setSectionInitialSelection = (category, resource) => {
    let c = 0;
    let privilege;
    const resourceKey = `${category}-${resource}`;
    selectedPrivileges.forEach((id) => {
      privilege = privileges.find((priv) => priv.id === id);
      if (privilege) {
        const titleParts = privilege.Resource.title.split(' / ');
        const privCategory = titleParts[0] || 'Other';
        const privResource = titleParts[1] || privilege.Resource.title;
        if (privCategory === category && privResource === resource) {
          c++;
        }
      }
    });
    setIsSectionSelected((prev) => ({
      ...prev,
      [resourceKey]: c === sections[category][resource].length,
    }));
  };

  const handleNameChange = (value) => {
    setName(value);
  };
  const handleDescriptionChange = (value) => {
    setDescription(value);
  };
  const handleCheckboxChange = (priv, index) => {
    const result = processCheckboxChangeWithReadEnforcement({
      priv,
      index,
      boxesState,
      privileges,
      sections,
      manuallyCheckedReads,
    });

    if (!result) return;

    const { newBoxesState, newSelected, updatedManualReads } = result;
    setBoxesState(newBoxesState);
    setSelectedPrivileges(newSelected);
    setManuallyCheckedReads(updatedManualReads);
    setIsSectionSelected(computeSectionSelectionState(sections, newSelected));
  };

  const handleSectionSelection = (category, resource) => {
    const resourceKey = `${category}-${resource}`;
    let currentSelected = [...selectedPrivileges];
    const sectionPriviliges = sections[category][resource];
    const isCurrentlySelected = isSectionSelected[resourceKey];

    const newManualReads = new Set(manuallyCheckedReads);
    if (isCurrentlySelected) {
      sectionPriviliges.forEach((priv) => {
        currentSelected = currentSelected.filter((id) => id !== priv.id);
      });
      newManualReads.delete(resourceKey);
    } else {
      sectionPriviliges.forEach((priv) => {
        if (!currentSelected.includes(priv.id)) {
          currentSelected.push(priv.id);
        }
      });
      newManualReads.add(resourceKey);
    }

    setSelectedPrivileges(currentSelected);
    setManuallyCheckedReads(newManualReads);

    const updatedBoxes = [...boxesState];
    privileges.forEach((priv, index) => {
      updatedBoxes[index] = currentSelected.includes(priv.id);
    });
    setBoxesState(updatedBoxes);

    setIsSectionSelected((prevState) => ({
      ...prevState,
      [resourceKey]: !isCurrentlySelected,
    }));
  };

  const handleSectionClearAll = (category, resource) => {
    const resourceKey = `${category}-${resource}`;
    let currentSelected = [...selectedPrivileges];
    const sectionPriviliges = sections[category][resource];

    sectionPriviliges.forEach((priv) => {
      currentSelected = currentSelected.filter((id) => id !== priv.id);
    });

    const newManualReads = new Set(manuallyCheckedReads);
    newManualReads.delete(resourceKey);

    setSelectedPrivileges(currentSelected);
    setManuallyCheckedReads(newManualReads);

    const updatedBoxes = [...boxesState];
    privileges.forEach((priv, index) => {
      updatedBoxes[index] = currentSelected.includes(priv.id);
    });
    setBoxesState(updatedBoxes);

    setIsSectionSelected((prevState) => ({
      ...prevState,
      [resourceKey]: false,
    }));
  };
  const handleSelectAll = () => {
    const newSectionState = {};
    const newManualReads = new Set();

    if (selectedPrivileges.length === privileges.length) {
      setSelectedPrivileges([]);
      setBoxesState(new Array(privileges.length).fill(false));
      Object.entries(sections).forEach(([category, resources]) => {
        Object.keys(resources).forEach((resource) => {
          newSectionState[`${category}-${resource}`] = false;
        });
      });
    } else {
      setSelectedPrivileges(privileges.map((priv) => priv.id));
      setBoxesState(new Array(privileges.length).fill(true));
      Object.entries(sections).forEach(([category, resources]) => {
        Object.keys(resources).forEach((resource) => {
          const resourceKey = `${category}-${resource}`;
          newSectionState[resourceKey] = true;
          newManualReads.add(resourceKey);
        });
      });
    }

    setManuallyCheckedReads(newManualReads);
    setIsSectionSelected(newSectionState);
  };

  const saveRole = useCallback(
    async (customUsersStrategy = null) => {
      const formData = {
        name: name,
        description: description,
        privileges: selectedPrivileges,
        published: published,
      };

      if (roleGroupType) {
        formData.is_internal = roleGroupType === PRIVILEGE_TYPES.INTERNAL;
        formData.is_external = roleGroupType === PRIVILEGE_TYPES.EXTERNAL;
      }

      if (customUsersStrategy) {
        formData.customUsersStrategy = customUsersStrategy;
        formData.initialPrivileges = initialRolePrivileges;
      }

      try {
        if (!id) {
          await axiosInstance.post(
            '/api/roles-privileges/role-privileges',
            formData
          );
          toast.success(t('Role Created Successfully'), {
            position: 'top-center',
          });
        } else {
          await axiosInstance.put(`/api/roles-privileges/${id}`, formData);
          toast.success(t('Role Updated Successfully'), {
            position: 'top-center',
          });
        }
        navigateBack();
        return true;
      } catch (error) {
        const msg = error.response?.data?.message;
        toast.error(msg || t('privilegesSection.saveError'), {
          position: 'top-center',
        });
        return false;
      }
    },
    [
      name,
      description,
      selectedPrivileges,
      published,
      roleGroupType,
      initialRolePrivileges,
      id,
      t,
      navigateBack,
    ]
  );

  const handleSubmit = async () => {
    if (caseProp === 'role' && !name?.trim()) {
      toast.error(t('role_group_name_required'), {
        position: 'top-center',
      });
      return false;
    }

    if (selectedPrivileges.length === 0) {
      toast.error(t('privilegesSection.minPrivilegeError'), {
        position: 'top-center',
      });
      return false;
    }

    if (caseProp === 'role') {
      if (id) {
        try {
          const customUsersResult =
            await getUsersWithCustomPrivilegesForRole(id);
          if (customUsersResult.hasCustomUsers) {
            setCustomUsersData(customUsersResult);
            setShowCustomUsersModal(true);
            return false;
          }
        } catch (error) {
          console.error('Error checking custom users:', error);
        }
      }

      return await saveRole();
    } else if (caseProp === 'user') {
      try {
        await axiosInstance.put(`/api/users-priviliges/`, {
          privileges: selectedPrivileges,
          userId: id,
        });
        toast.success(t('Privilegs Updated Successfully'), {
          position: 'top-center',
        });
        navigateBack();
        return true;
      } catch (error) {
        const msg = error.response?.data?.message;
        toast.error(msg || t('privilegesSection.saveError'), {
          position: 'top-center',
        });
        return false;
      }
    }
  };

  const handleCustomUsersStrategySelect = async (strategy) => {
    setShowCustomUsersModal(false);
    await saveRole(strategy);
  };

  const handleCustomUsersModalClose = () => {
    setShowCustomUsersModal(false);
    setCustomUsersData(null);
  };

  const handleRoleGroupTypeChange = (value) => {
    setRoleGroupType(value);
  };

  // Validation - use local state since form fields update local state, not Formik values
  const validate = () => {
    const errors = {};
    // Only validate role-specific fields when caseProp is 'role'
    if (caseProp === 'role') {
      if (!name) {
        errors.name = t('role_group_name_required');
      }
      if (!roleGroupType) {
        errors.roleGroupType = t('role_group_type_required');
      }
    }
    return errors;
  };

  // Don't render the form until translations are ready
  if (!ready) {
    return (
      <Grid container spacing={2} className={styles.gridContainer}>
        <Grid size={12}>
          <div className={styles.loadingContainer}>
            <CircularProgress />
          </div>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} className={styles.gridContainer}>
      <Grid size={12}>
        <Formik
          initialValues={{
            name,
            description,
            privileges,
            published: false,
            roleGroupType,
          }}
          validate={validate}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          <Form noValidate>
            {caseProp === 'role' && !privilegesOnly && (
              <>
                <FormikTextField
                  fullWidth
                  name="name"
                  label={t('role_group_name')}
                  required
                  value={name || ''}
                  onChange={(e) => handleNameChange(e.target.value)}
                />

                <FormikTextField
                  fullWidth
                  name="description"
                  label={t('role_group_description')}
                  // required
                  multiline
                  value={description || ''}
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                />

                <FormikSelect
                  name="roleGroupType"
                  label={t('role_group_type')}
                  fullWidth
                  required
                  onChange={(e) => handleRoleGroupTypeChange(e.target.value)}
                  disabled={id ? true : false}
                >
                  {privilegesTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(type).charAt(0).toUpperCase() + t(type).slice(1)}
                    </MenuItem>
                  ))}
                </FormikSelect>
              </>
            )}

            {caseProp &&
            caseProp === 'role' &&
            !roleGroupType &&
            !privilegesOnly ? (
              <></>
            ) : (
              <>
                <PrivilegesSections
                  sections={sections}
                  isSectionSelected={isSectionSelected}
                  published={published}
                  privileges={privileges}
                  boxesState={boxesState}
                  handleSectionClearAll={handleSectionClearAll}
                  handleSectionSelection={handleSectionSelection}
                  handleCheckboxChange={handleCheckboxChange}
                  capitalizeFirstLetter={capitalizeFirstLetter}
                  selectedPrivileges={selectedPrivileges}
                  handleSelectAll={handleSelectAll}
                />
              </>
            )}

            <div className={styles.actionsFooter}>
              {caseProp === 'role' &&
                roleGroupType &&
                !privilegesOnly &&
                !id && (
                  <Field name="published">
                    {({ field, meta }) => (
                      <div className={styles.publishedCheckbox}>
                        <CheckboxInput
                          {...field}
                          meta={meta}
                          label={t('published')}
                          value={published}
                          checked={published}
                          onChange={(checked) => {
                            setPublished(checked);
                          }}
                        />
                      </div>
                    )}
                  </Field>
                )}
              <Button
                variant="primary"
                type="submit"
                className={`${styles.submitButton} ${privilegesOnly ? styles.submitButtonHidden : ''}`}
              >
                {t('save')}
              </Button>
            </div>
          </Form>
        </Formik>

        <CustomUsersModal
          open={showCustomUsersModal}
          onClose={handleCustomUsersModalClose}
          onSelectStrategy={handleCustomUsersStrategySelect}
          usersCount={customUsersData?.count || 0}
        />
      </Grid>
    </Grid>
  );
};

export default PrivilegesForm;
